import React from 'react';
import type {
    ImageCropUploadValue,
    ImageUploadPayload,
} from '@/components/imageCropUpload';
import { useResetOnKeyChange } from '@/utils';

export type CategoryFormMode = 'create' | 'edit';

/**
 * Sentinel option value for "no parent (top-level)". The parent picker can't
 * use a real `null` ListBox key, so the modal maps this value to `null` on the
 * way out and back.
 */
export const NO_PARENT_VALUE = '__none__';

export type CategoryFormInitialValues = {
    name: string;
    slug: string;
    parentId: string | null;
    image: ImageCropUploadValue | null;
};

/**
 * What the modal hands back when the user submits. `imageUrl` carries the
 * *existing* URL on edits (or `null` when the image was cleared); `imageUpload`
 * is only populated when the user picked a fresh image that still needs to be
 * uploaded. `parentId` is the chosen parent (`null` = top-level). The submit
 * handler decides whether to upload first and which URL to persist.
 */
export type CategoryFormSubmit = {
    imageUpload: ImageUploadPayload | null;
    imageUrl: string | null;
    name: string;
    parentId: string | null;
    slug: string;
};

type UseCategoryFormParams = {
    initialValues?: CategoryFormInitialValues;
    isOpen: boolean;
    mode?: CategoryFormMode;
    onSubmit: (input: CategoryFormSubmit) => void;
};

const EMPTY_INITIAL: CategoryFormInitialValues = {
    name: '',
    slug: '',
    parentId: null,
    image: null,
};

/** Derive a URL-safe slug from arbitrary text (used for the auto-slug). */
export const slugify = (value: string): string =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

/**
 * Sanitize a slug the user is typing by hand. Unlike `slugify`, this preserves
 * the `-` and `_` separators the user enters (and any trailing one, so they can
 * keep typing) while still lowercasing and stripping otherwise-invalid chars.
 */
export const sanitizeSlugInput = (value: string): string =>
    value.toLowerCase().replace(/[^a-z0-9_-]+/g, '-');

/**
 * Form state for the Add / Edit Category modal. Keeps the name, slug and
 * cropped image in sync, auto-derives the slug from the name in create mode
 * until the user edits the slug by hand, and resets to `initialValues` (or
 * blanks) every time the modal closes so a reopened dialog always starts in a
 * known state.
 */
export const useCategoryForm = ({
    initialValues,
    isOpen,
    mode = 'create',
    onSubmit,
}: UseCategoryFormParams) => {
    const initial = initialValues ?? EMPTY_INITIAL;

    const [image, setImage] = React.useState<ImageCropUploadValue | null>(
        initial.image,
    );
    const [name, setName] = React.useState(initial.name);
    const [slug, setSlug] = React.useState(initial.slug);
    const [parentId, setParentId] = React.useState<string | null>(
        initial.parentId,
    );
    // In edit mode we don't auto-derive the slug from the name — the slug is
    // pre-seeded from the existing category and the user typing the name
    // mustn't quietly rename the slug too.
    const [isSlugEdited, setIsSlugEdited] = React.useState(mode === 'edit');

    // Re-seed every field from initialValues on each open/close transition.
    // The modal is always mounted, so the lazy `useState` seeds only once —
    // without this, clicking Edit would never populate the row's existing
    // values (and a reopened dialog could show stale input). Keyed on `isOpen`
    // only, so typing mid-edit isn't clobbered if initialValues recompute
    // (e.g. a background refetch).
    useResetOnKeyChange(isOpen, () => {
        setImage(initial.image);
        setName(initial.name);
        setSlug(initial.slug);
        setParentId(initial.parentId);
        setIsSlugEdited(mode === 'edit');
    });

    const onNameChange = React.useCallback(
        (value: string) => {
            setName(value);
            if (!isSlugEdited) {
                setSlug(slugify(value));
            }
        },
        [isSlugEdited],
    );

    const onSlugChange = React.useCallback((value: string) => {
        setIsSlugEdited(true);
        setSlug(sanitizeSlugInput(value));
    }, []);

    const onImageChange = React.useCallback(
        (value: ImageCropUploadValue | null) => {
            setImage(value);
        },
        [],
    );

    const onParentChange = React.useCallback((value: string | null) => {
        setParentId(value);
    }, []);

    const trimmedName = name.trim();
    const isValid = trimmedName.length > 0 && slug.length > 0;

    // We deliberately do NOT close the modal here — the parent's submit
    // handler (`useCategoriesPage.onSubmitCategory`) owns the close decision:
    // close on success, keep open on failure so the inline `formError` stays
    // visible and the user can retry without re-entering everything. Mirrors
    // `useSupplierForm`.
    const onSubmitClick = React.useCallback(() => {
        if (!isValid) {
            return;
        }
        onSubmit({
            imageUpload: image?.imageUpload ?? null,
            imageUrl: image?.imageUpload ? null : (image?.imageUrl ?? null),
            name: trimmedName,
            parentId,
            slug,
        });
    }, [image, isValid, onSubmit, parentId, slug, trimmedName]);

    return {
        image,
        isValid,
        name,
        onImageChange,
        onNameChange,
        onParentChange,
        onSubmit: onSubmitClick,
        onSlugChange,
        parentId,
        slug,
    };
};
